import { HttpClient } from '@angular/common/http';
import { inject, Injectable, provideEnvironmentInitializer, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PalantirDialogService } from '@flux/design-system';
import { catchError, filter, firstValueFrom, of, switchMap, timer } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { fluxLogger } from '../logger/logger.instance';
import { BlogNotificationDialogComponent } from '../../../shared/components/blog-notification-dialog/blog-notification-dialog.component';
import { BlogFeed, BlogFeedItem } from './blog-notification.types';
import { FeatureFlagService } from '../feature-flag/feature-flag.service.js';
import { FeatureFlagsEnum } from '../../../../generated/featureFlags.enum.js';
import { environment } from '../../../../environments/environment.js';

const logger = fluxLogger.extend('blog-notification');

@Injectable({
  providedIn: 'root',
})
export class BlogNotificationService {
  private static readonly BLOG_FEED_URL =
    environment.ENVIRONMENT !== 'development' ? '/wiki/blog/feed.json' : '/assets/feed.json';
  private static readonly LAST_SEEN_KEY = 'FLUX_CONTROL.BLOG_LAST_SEEN';
  private static readonly INITIAL_DELAY_MS = 3000;

  private readonly http = inject(HttpClient);
  private readonly dialogService = inject(PalantirDialogService);
  private readonly authService = inject(AuthService);
  private readonly featureFlagsService = inject(FeatureFlagService);

  public readonly isModalOpen = signal(false);

  constructor() {
    this.authService.authenticatedUser$
      .pipe(
        takeUntilDestroyed(),
        filter((user) => Boolean(user)),
        switchMap(() => timer(BlogNotificationService.INITIAL_DELAY_MS)),
      )
      .subscribe(() => {
        void this.checkAndShowBlogPosts();
      });
  }

  public async checkAndShowBlogPosts(): Promise<void> {
    if (!this.authService.authenticatedUser()) {
      return;
    }

    if (!(await this.featureFlagsService.isFeatureFlagEnabled(FeatureFlagsEnum.FeatureBlogPosts))) {
      logger.debug('Blog posts feature flag is disabled, skipping check');
      return;
    }

    logger.debug('Checking for new blog posts...');
    try {
      const feed = await firstValueFrom(
        this.http.get<BlogFeed>(BlogNotificationService.BLOG_FEED_URL).pipe(
          catchError((error) => {
            logger.warn('Failed to fetch blog feed', error);
            return of(null);
          }),
        ),
      );

      if (!feed?.items?.length) {
        logger.debug('No items found in blog feed');
        return;
      }

      const sortedItems = [...feed.items].sort(
        (a, b) => new Date(b.date_modified).getTime() - new Date(a.date_modified).getTime(),
      );

      const latestPost = sortedItems[0];
      const lastSeenDateStr = localStorage.getItem(BlogNotificationService.LAST_SEEN_KEY);
      const lastSeenDate = lastSeenDateStr ? new Date(lastSeenDateStr).getTime() : 0;
      const latestPostDate = new Date(latestPost.date_modified).getTime();

      logger.debug('Blog feed check', {
        latestPostDate: latestPost.date_modified,
        lastSeenDate: lastSeenDateStr,
        shouldShow: latestPostDate > lastSeenDate,
      });

      if (latestPostDate > lastSeenDate) {
        this.openDialog(sortedItems, latestPost.date_modified);
      }
    } catch (error) {
      logger.error('Unexpected error checking blog posts', error);
    }
  }

  public async openManuallyWithAllPosts(): Promise<void> {
    if (!this.authService.authenticatedUser()) {
      return;
    }

    if (!(await this.featureFlagsService.isFeatureFlagEnabled(FeatureFlagsEnum.FeatureBlogPosts))) {
      logger.debug('Blog posts feature flag is disabled, cannot open blog notifications');
      return;
    }

    logger.debug('Checking for new blog posts...');
    try {
      const feed = await firstValueFrom(
        this.http.get<BlogFeed>(BlogNotificationService.BLOG_FEED_URL).pipe(
          catchError((error) => {
            logger.warn('Failed to fetch blog feed', error);
            return of(null);
          }),
        ),
      );

      if (!feed?.items?.length) {
        logger.debug('No items found in blog feed');
        return;
      }

      const sortedItems = [...feed.items].sort(
        (a, b) => new Date(b.date_modified).getTime() - new Date(a.date_modified).getTime(),
      );

      this.openDialog(sortedItems, sortedItems[0].date_modified, true);
    } catch (error) {
      logger.error('Unexpected error checking blog posts', error);
    }
  }

  private openDialog(posts: BlogFeedItem[], latestDate: string, showAll = false): void {
    if (this.isModalOpen()) {
      return;
    }

    this.isModalOpen.set(true);
    const lastSeenDateStr = localStorage.getItem(BlogNotificationService.LAST_SEEN_KEY);
    const lastSeenDate = lastSeenDateStr ? new Date(lastSeenDateStr).getTime() : 0;

    const displayPosts = showAll
      ? posts
      : posts.filter((p) => new Date(p.date_modified).getTime() > lastSeenDate);

    logger.info('Opening blog notification dialog', {
      postCount: displayPosts.length,
      latestDate,
    });

    const dialogRef = this.dialogService.openDialog(BlogNotificationDialogComponent, {
      size: 'large',
      data: {
        posts: displayPosts,
      },
    });

    void dialogRef.afterClosed().then(() => {
      logger.debug('Dialog closed, updating last seen date', latestDate);
      localStorage.setItem(BlogNotificationService.LAST_SEEN_KEY, latestDate);
      this.isModalOpen.set(false);
    });
  }

  public static provider() {
    return provideEnvironmentInitializer(() => {
      inject(BlogNotificationService);
    });
  }
}
